import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { HorizontalScrollContainer } from '@atlassian/conversation-assistant-ui-components';
import { PersonCard } from '@atlassian/people-teams-ui/directory';
import { useActivityClient } from '@atlassian/recent-work-client';
import { useAIAnswerContext } from '../ai-answer-dialog/ai-answer-context';
import { NLPSearchErrorState, NLPSearchResultFormat } from '../ai-answer-dialog/types';
import { getTypedJSONResultData } from '../ai-answer-dialog/utils/getTypedJSONResultData';
import { useAnalytics } from '../common/controller/analytics';
import { KnowledgeCarousel } from '../knowledge-card-components';
import { onKnowledgeCardShown } from './analytics';
import { fetchPrimaryUserData } from './gql/hydrate-people-card';
import { KnowledgeCardsBody } from './knowledge-cards-body';
import { KnowledgeCardsHeader } from './knowledge-cards-header';
import { SeparatorLine } from './separator-line';
import { KnowledgeCardsIntentType } from './types';
import { formatTimeKnowledgeCard } from './util';
var ScrollableContainerStyles = xcss({
  padding: 'space.100',
  margin: 'auto',
  marginBottom: 'space.100',
  display: 'flex'
});
var CardContainerStyles = xcss({
  borderRadius: "var(--ds-border-radius-100, 4px)",
  borderWidth: "var(--ds-border-width, 1px)",
  borderStyle: 'solid',
  borderColor: 'color.border'
});
var PersonCardWrapperStyles = xcss({
  width: '180px',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  height: '100%'
});
var USERS_SCROLL_OFFSET = 392;
var messages = defineMessages({
  multiple_people_matches_header: {
    id: 'search_ai.knowledge_cards.multiple_people_matches_header',
    defaultMessage: 'People matches',
    description: 'Header for the multiple people carousel'
  }
});
var IndividualPersonKnowledgeCard = function IndividualPersonKnowledgeCard(_ref) {
  var _primaryUser$account_;
  var primaryUser = _ref.primaryUser;
  if (!primaryUser) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Stack, {
    xcss: CardContainerStyles
  }, /*#__PURE__*/React.createElement(KnowledgeCardsHeader, {
    type: KnowledgeCardsIntentType.PERSON,
    accountId: primaryUser.account_id,
    name: primaryUser.name,
    email: primaryUser.email,
    avatarUrl: primaryUser.avatar_url,
    profileUrl: primaryUser.profile_url,
    jobTitle: primaryUser.job_title,
    department: primaryUser.department,
    location: primaryUser.location,
    time: primaryUser.time
  }), /*#__PURE__*/React.createElement(SeparatorLine, null), /*#__PURE__*/React.createElement(KnowledgeCardsBody, {
    type: KnowledgeCardsIntentType.PERSON,
    currentUserId: (_primaryUser$account_ = primaryUser.account_id) !== null && _primaryUser$account_ !== void 0 ? _primaryUser$account_ : '',
    recommendedUsers: primaryUser.collaborators || [],
    recentActivity: primaryUser.recentActivity || [],
    profileUrl: primaryUser.profile_url || ''
  }));
};
export var PersonKnowledgeCard = function PersonKnowledgeCard() {
  var _useAIAnswerContext = useAIAnswerContext(),
    commonAttributes = _useAIAnswerContext.commonAttributes,
    requestingUserId = _useAIAnswerContext.userDetails.id,
    setData = _useAIAnswerContext.setData;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage,
    locale = _useIntl.locale;
  var activityClient = useActivityClient('v3', 'people');
  var _useAnalytics = useAnalytics(),
    _useAnalytics2 = _slicedToArray(_useAnalytics, 1),
    fireAnalyticsEvent = _useAnalytics2[0];
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    selectedUser = _useState2[0],
    setSelectedUser = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    primaryUser = _useState4[0],
    setPrimaryUser = _useState4[1];
  var answerString = commonAttributes.answerString,
    cloudId = commonAttributes.cloudId;
  var userResults = getTypedJSONResultData(answerString).parsedResult;
  var ScrollWrapper = userResults.length > 3 ? function (props) {
    return /*#__PURE__*/React.createElement(HorizontalScrollContainer, null, /*#__PURE__*/React.createElement(Box, {
      xcss: ScrollableContainerStyles
    }, props.children));
  } : React.Fragment;
  var getHandleSelectUser = useCallback(function (index) {
    return function () {
      setSelectedUser(index);
    };
  }, []);
  useEffect(function () {
    if (userResults.length === 1) {
      setSelectedUser(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userResults]);
  useEffect(function () {
    var fetchData = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              if (!(selectedUser !== undefined)) {
                _context.next = 2;
                break;
              }
              return _context.abrupt("return", fetchPrimaryUserData({
                accountId: userResults[selectedUser].id,
                tenantId: cloudId,
                requestingUserId: requestingUserId || '',
                activityClient: activityClient,
                locale: locale
              }).then(function (data) {
                setPrimaryUser(data);
              }).catch(function (error) {
                setData({
                  errorState: NLPSearchErrorState.NetworkError,
                  disclaimer: null,
                  format: NLPSearchResultFormat.JSON,
                  nlpResults: [],
                  uniqueSources: [],
                  nlpFollowUpResults: null
                });
              }));
            case 2:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return function fetchData() {
        return _ref2.apply(this, arguments);
      };
    }();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);
  useEffect(function () {
    fireAnalyticsEvent(onKnowledgeCardShown(_objectSpread(_objectSpread({}, commonAttributes), {}, {
      cardType: KnowledgeCardsIntentType.PERSON
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return selectedUser === undefined && userResults.length > 1 ?
  // adds horizontal scroll when more than 3 users
  fg('kd_smart_answers_update_person_carousel') ? /*#__PURE__*/React.createElement(KnowledgeCarousel, {
    headingText: formatMessage(messages.multiple_people_matches_header),
    scrollOffset: USERS_SCROLL_OFFSET
  }, /*#__PURE__*/React.createElement(Inline, {
    space: "space.200"
  }, userResults.map(function (user, index) {
    var _user$title, _user$location;
    return (
      /*#__PURE__*/
      // eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
      React.createElement(Box, {
        onClick: getHandleSelectUser(index),
        xcss: PersonCardWrapperStyles,
        testId: "person-knowledge-card-wrapper",
        key: user.id
      }, /*#__PURE__*/React.createElement(PersonCard, {
        data: {
          id: user.id,
          name: user.name,
          jobTitle: (_user$title = user.title) !== null && _user$title !== void 0 ? _user$title : '',
          avatarUrl: user.avatarUrl,
          localTime: formatTimeKnowledgeCard({
            locale: locale,
            zoneInfo: user.zoneInfo
          }),
          location: (_user$location = user.location) !== null && _user$location !== void 0 ? _user$location : ''
        }
      }))
    );
  }))) : /*#__PURE__*/React.createElement(ScrollWrapper, null, /*#__PURE__*/React.createElement(Inline, {
    space: "space.200"
  }, userResults.map(function (user, index) {
    var _user$title2, _user$location2;
    return (
      /*#__PURE__*/
      // eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
      React.createElement(Box, {
        onClick: getHandleSelectUser(index),
        xcss: PersonCardWrapperStyles,
        testId: "person-knowledge-card-wrapper",
        key: user.id
      }, /*#__PURE__*/React.createElement(PersonCard, {
        data: {
          id: user.id,
          name: user.name,
          jobTitle: (_user$title2 = user.title) !== null && _user$title2 !== void 0 ? _user$title2 : '',
          avatarUrl: user.avatarUrl,
          localTime: formatTimeKnowledgeCard({
            locale: locale,
            zoneInfo: user.zoneInfo
          }),
          location: (_user$location2 = user.location) !== null && _user$location2 !== void 0 ? _user$location2 : ''
        }
      }))
    );
  }))) : /*#__PURE__*/React.createElement(IndividualPersonKnowledgeCard, {
    primaryUser: primaryUser
  });
};